import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import FAQSection from "@/components/products/FAQSection";
import { submitToZoho } from "@/utils/secureFormSubmission";

const Enterprise = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.company) return;

    setIsSubmitting(true);
    try {
      const success = await submitToZoho({
        ...formData,
        formType: "Enterprise Solution"
      });

      if (success) {
        toast.success("Form submitted successfully! We'll be in touch soon.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          message: ""
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <section className="bg-gradient-to-b from-slate-50 to-white py-10 md:py-16 lg:py-24">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
                  Enterprise-Grade <span className="text-[#8932c8]">Security</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8">
                  Protect your large-scale organization with Spharaka's enterprise security solutions. Our advanced XDR platform delivers comprehensive protection across your entire digital ecosystem.
                </p>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-[#8932c8] hover:bg-[#7a2cb3] w-full md:w-auto px-6 md:px-8 py-5 md:py-6 text-base md:text-lg">
                      Request Enterprise Demo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px] w-[95vw] max-w-[95vw] sm:w-auto">
                    <DialogHeader>
                      <DialogTitle className="text-xl md:text-2xl font-bold">Request Enterprise Demo</DialogTitle>
                      <DialogDescription>
                        Fill out the form below and our enterprise team will contact you to schedule a personalized demo.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 py-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-medium mb-1">First Name</label>
                          <Input 
                            id="firstName" 
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="John" 
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-medium mb-1">Last Name</label>
                          <Input 
                            id="lastName" 
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Doe" 
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-1">Work Email</label>
                        <Input 
                          id="email" 
                          type="email" 
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john.doe@enterprise.com" 
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium mb-1">Company</label>
                        <Input 
                          id="company" 
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Enterprise Inc." 
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium mb-1">Enterprise Security Needs</label>
                        <Textarea 
                          id="message" 
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your enterprise security challenges..." 
                        />
                      </div>
                      <Button type="submit" className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]">
                        Submit Request
                      </Button>
                      <p className="text-xs text-gray-500 text-center mt-4">
                        By submitting this form, you agree to our privacy policy and terms of service.
                      </p>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1600&q=80"
                  alt="Enterprise Security"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 md:mb-12 text-center">Enterprise Security Challenges</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
              <div className="bg-slate-50 p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Complex Infrastructure</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Enterprise environments include diverse networks, clouds, and endpoints that create complex security challenges.
                </p>
              </div>
              <div className="bg-slate-50 p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Advanced Threats</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Enterprises face sophisticated attacks specifically targeted at high-value organizational assets.
                </p>
              </div>
              <div className="bg-slate-50 p-4 md:p-6 rounded-lg shadow-sm">
                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Regulatory Compliance</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  Meeting complex compliance requirements across multiple jurisdictions requires comprehensive security measures.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 md:mb-12 text-center">Spharaka XDR for Enterprise</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="md:order-1 order-2">
                <div className="space-y-4 md:space-y-6">
                  <div className="space-y-2">
                    <h3 className="text-lg md:text-xl font-semibold">Unified Security Visibility</h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      Gain complete visibility across your entire enterprise IT infrastructure, from cloud workloads to on-premise systems.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg md:text-xl font-semibold">Autonomous Threat Response</h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      Our Agentic AI automatically investigates and remediates threats without human intervention, reducing response times.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg md:text-xl font-semibold">Enterprise-Scale Analytics</h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      Process billions of security events daily with our cloud-native architecture designed for enterprise workloads.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg md:text-xl font-semibold">Compliance Automation</h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      Automatically generate compliance reports and maintain audit trails for regulatory requirements.
                    </p>
                  </div>
                </div>
              </div>
              <div className="md:order-2 order-1 mb-6 md:mb-0">
                <img
                  src="https://images.unsplash.com/photo-1496096265110-f83ad7f96608?auto=format&fit=crop&w=1600&q=80"
                  alt="Enterprise XDR Solution"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <FAQSection />

        <section className="py-10 md:py-16 bg-[#8932c8] text-white">
          <div className="container mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6">Ready to transform your enterprise security?</h2>
            <p className="text-base md:text-xl mb-6 md:mb-10 max-w-3xl mx-auto">
              Join other enterprise organizations that have strengthened their security posture with Spharaka XDR.
            </p>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="bg-white text-[#8932c8] hover:bg-gray-100 border-white w-full md:w-auto px-6 md:px-8 py-5 md:py-6 text-base md:text-lg">
                  Schedule a Consultation
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] w-[95vw] max-w-[95vw] sm:w-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl md:text-2xl font-bold">Schedule a Consultation</DialogTitle>
                  <DialogDescription>
                    Fill out the form below to schedule a consultation with our enterprise security experts.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium mb-1">First Name</label>
                      <Input 
                        id="firstName" 
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John" 
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium mb-1">Last Name</label>
                      <Input 
                        id="lastName" 
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe" 
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1">Work Email</label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.doe@enterprise.com" 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-1">Company</label>
                    <Input 
                      id="company" 
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Enterprise Inc." 
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1">Message</label>
                    <Textarea 
                      id="message" 
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What specific security challenges are you facing?" 
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]">
                    Submit
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-4">
                    By submitting this form, you agree to our privacy policy and terms of service.
                  </p>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Enterprise;
